import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  Signal,
} from '@angular/core';

import { Button } from 'primeng/button';
import {
  CurrencyComponent,
  Icon,
  IconComponent,
  PrimengButtonDirective,
  PrimengDialogDirective,
  PrimengDividerDirective,
} from '@web/shared/ui';
import { Divider } from 'primeng/divider';
import { isNil, isNotNil, Nillable, valueOrDefault } from '@web/utils';
import { Dialog } from 'primeng/dialog';
import { PublicShippingMethodsComponent } from '../../containers/public-shipping-methods/public-shipping-methods.component';
import {
  PublicCartDetails,
  PublicShippingPriceType,
} from '@web/shared/module/shop';
import { PublicOrderSummaryRecursiveInfoComponent } from '../public-order-summary-recursive-info/public-order-summary-recursive-info.component';
import { PublicOrderSummaryAdditionalShippingCostInfoComponent } from '../public-order-summary-additional-shipping-cost-info/public-order-summary-additional-shipping-cost-info.component';
import { SummernoteHtmlPreviewComponent } from '@web/shared/external';

@Component({
  selector: 'app-public-cart-summary',
  imports: [
    Button,
    CurrencyComponent,
    Divider,
    PrimengButtonDirective,
    PrimengDividerDirective,
    IconComponent,
    Dialog,
    PublicShippingMethodsComponent,
    PrimengDialogDirective,
    PublicOrderSummaryRecursiveInfoComponent,
    PublicOrderSummaryAdditionalShippingCostInfoComponent,
    SummernoteHtmlPreviewComponent,
  ],
  templateUrl: './public-cart-summary.component.html',
  styleUrl: './public-cart-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicCartSummaryComponent {
  public readonly details = input.required<PublicCartDetails>();
  public readonly checkoutClickEvent = output<void>();

  protected shippingTypesVisible = false;

  /* prettier-ignore */
  protected readonly totalItemsPrice = computed(() => this.details().summary.totalItemsPrice);
  /* prettier-ignore */
  protected readonly shippingPrice = computed(() => this.details().shipping.price);
  /* prettier-ignore */
  protected readonly totalPrice = computed(() => this.details().summary.totalPrice);
  protected readonly discount = computed(() => this.details().discount);
  protected readonly recursive = computed(() => this.details().recursive);
  /* prettier-ignore */
  protected readonly isPredictablePriceType = this.isPredictablePriceTypeSignal();
  /* prettier-ignore */
  protected readonly selectedShippingMethod = this.selectedShippingMethodSignal();
  /* prettier-ignore */
  protected readonly selectedShippingMethodDescription = this.selectedShippingMethodDescriptionSignal();
  /* prettier-ignore */
  protected readonly shouldShowSelectedShippingMethod = this.shouldShowSelectedShippingMethodSignal();
  protected readonly Icon = Icon;
  protected readonly isNil = isNil;
  protected readonly isNotNil = isNotNil;

  public handleShippingClick(): void {
    this.shippingTypesVisible = true;
  }

  public handleCheckoutClick(): void {
    this.checkoutClickEvent.emit();
  }

  /* prettier-ignore */
  private isPredictablePriceTypeSignal(): Signal<boolean> {
    return computed(() => this.details().shipping.priceType === PublicShippingPriceType.Predicted);
  }

  private shouldShowSelectedShippingMethodSignal(): Signal<boolean> {
    return computed(() => {
      const shipping = this.details().shipping;
      const { method, priceType } = shipping;

      return priceType === PublicShippingPriceType.Final && isNotNil(method);
    });
  }

  private selectedShippingMethodSignal(): Signal<string> {
    return computed(() => {
      const shipping = this.details().shipping;
      const { method } = shipping;

      return valueOrDefault(method?.name, '');
    });
  }

  private selectedShippingMethodDescriptionSignal(): Signal<Nillable<string>> {
    return computed(() => {
      const shipping = this.details().shipping;
      const { method } = shipping;

      return method?.description;
    });
  }
}
